import { debounce } from "lodash";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { getContractAsync, getMemberContractManageListAsync, postMemberContractManageInviteAsync } from "src/api/contract/contract-api";
import { ContractModel, MemberContractManageListParams, MemberContractManageModel } from "src/api/contract/contract-types";
import { useApiOperation } from "src/api/hooks";
import { usePartnerNavigate } from "src/api/hooks/usePartnerNavigate";
import { ConfirmModal, Spinner } from "src/components";
import Header from "src/components/layout/Header";
import MetaTag from "src/components/layout/MetaTag";
import { getMypageTitle } from "src/utils/app-Info";
import useIntersectionObserver from "src/pages/hooks/intersection-observer";
import { useNavigateGoBack } from "src/pages/hooks/navigate-go-back";
import { getReplacedSearchText, numberToStringWithComma, sanitizePhoneOrEmailSearch } from "src/utils/common-util";
import { InviteResult, MEMBER_SORT } from "../user-types";
import UserItem from "./components/UserItem";

const PAGE_SIZE = 20;

type Modal = {
  isOpen: boolean;
};

/**
 * 마이페이지 메인 > 신청/계약 내역 > 상세 > 이용자 목록
 */
const UserList = () => {
  const { t } = useTranslation(["user", "common"]);

  const firstLoadUserListRef = useRef<boolean>(false);
  const userListCountRef = useRef<HTMLParagraphElement>(null);

  const { goBack } = useNavigateGoBack();

  const navigate = usePartnerNavigate();

  // path variable 계약 id
  const { contractId } = useParams();

  // 신청/계약 상세 조회 api
  const { executeAsync: getContract } = useApiOperation(getContractAsync);

  // 계약의 이용자 목록 조회 api
  const { executeAsync: getMemberContractManageList, loading: getMemberContractManageListLoading } =
    useApiOperation(getMemberContractManageListAsync);

  const { executeAsync: postMemberContractManageInvite } = useApiOperation(postMemberContractManageInviteAsync, { noHandleError: true });

  // 신청/계약 상세
  const [contract, setContract] = useState<ContractModel | null>(null);

  // 계약의 이용자 목록
  const [contractManages, setContractManages] = useState<Array<MemberContractManageModel>>([]);

  // 검색어
  const [searchText, setSearchText] = useState<string>("");

  // 현재 페이지
  const [page, setPage] = useState<number>(0);

  // 총 페이지
  const [totalPages, setTotalPages] = useState<number>(0);

  // 확인 버튼만 있는 알림 모달
  const [alertModal, setAlertModal] = useState<Modal>({ isOpen: false });
  const [inviteConfirmModal, setInviteConfirmModal] = useState<Modal>({ isOpen: false });
  const [inviteSuccessModal, setInviteSuccessModal] = useState<Modal>({ isOpen: false });
  const [inviteFailModal, setInviteFailModal] = useState<Modal>({ isOpen: false });
  const [inviteResults, setInviteResults] = useState<Array<InviteResult>>([]);
  const [isUserListCountCompact, setIsUserListCountCompact] = useState(false);
  const [clickedPhoneNumber, setClickedPhoneNumber] = useState<string>();

  // 분자는 계약의 현재 이용자 수다. 화면에 로드된 contractManages.length 를 쓰면
  // 무한 스크롤로 더 불러오기 전까지 실제보다 적은 값이 노출된다
  const userListCountText = t("userList.userCount", {
    value: numberToStringWithComma(contract?.memberTotCount || 0),
    max: numberToStringWithComma(contract?.memberMaxNums || 0),
  });

  useLayoutEffect(() => {
    const userListCountElement = userListCountRef.current;
    const parentElement = userListCountElement?.parentElement;

    if (!userListCountElement || !parentElement) return;

    // https://pnpt.atlassian.net/wiki/x/9wOQdQ
    // 디자인 요구사항
    // - 콤마 포맷 적용
    // - 2줄 넘어간다면 폰트 사이즈 16 → 14로 변경

    /**
     * 카운트 텍스트가 기본 16px 상태에서 2줄 이상으로 내려가는지 확인한다.
     *
     * 실제 카운트 엘리먼트에 compact 클래스가 이미 적용되어 있으면 높이가 14px 기준으로 줄어들어
     * "기본 16px였다면 2줄이었는지"를 정확히 판단하기 어렵다. 그래서 같은 텍스트/너비를 가진
     * 숨김 복제 엘리먼트를 만들고 compact 클래스를 제거한 뒤, 기본 스타일 기준의 줄 수를 측정한다.
     */
    const measureDefaultLineCount = () => {
      const clone = userListCountElement.cloneNode(true) as HTMLParagraphElement;
      const { width } = userListCountElement.getBoundingClientRect();

      if (width <= 0) return 1;

      // 레이아웃에는 영향을 주지 않으면서 실제 줄바꿈 계산은 브라우저 렌더링 엔진에 맡긴다.
      clone.classList.remove("user-list-count--compact");
      clone.style.position = "absolute";
      clone.style.visibility = "hidden";
      clone.style.pointerEvents = "none";
      clone.style.width = `${width}px`;
      clone.style.height = "auto";
      clone.style.maxHeight = "none";
      clone.style.left = "-9999px";
      clone.style.top = "0";

      parentElement.appendChild(clone);

      const computedStyle = window.getComputedStyle(clone);
      const lineHeight = parseFloat(computedStyle.lineHeight) || parseFloat(computedStyle.fontSize) * 1.2;
      const lineCount = Math.round(clone.scrollHeight / lineHeight);

      clone.remove();

      return lineCount;
    };

    const updateUserListCountSize = () => {
      // 기본 16px에서 2줄 이상이면 14px compact 스타일을 적용한다.
      setIsUserListCountCompact(measureDefaultLineCount() >= 2);
    };

    updateUserListCountSize();

    // 화면 너비나 부모 영역이 바뀌면 줄바꿈 여부도 달라질 수 있으므로 다시 측정한다.
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateUserListCountSize);

      return () => {
        window.removeEventListener("resize", updateUserListCountSize);
      };
    }

    const resizeObserver = new ResizeObserver(updateUserListCountSize);
    resizeObserver.observe(parentElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, [userListCountText]);

  // 이용자 목록 조회
  const loadUserList = useCallback(
    async (_page: number, searchValue?: string) => {
      if (!contract?.contractManageId) return;

      const list: MemberContractManageModel[] = [];
      const requestParams: MemberContractManageListParams = {
        contractManageId: contract.contractManageId,
        pageRequest: {
          page: _page,
          size: PAGE_SIZE,
          // 계약의 이용자 정렬 기준. 계약자 > 이용자 마스터 > 이용자 순, 같은 등급 안에서는 최근 초대순.
          sort: MEMBER_SORT,
        },
      };
      if (searchValue) {
        requestParams.searchType = "MEMBER_CONTACT";
        requestParams.searchValue = getReplacedSearchText(searchValue);
      }

      const { data } = await getMemberContractManageList(requestParams);
      list.push(...(data?.data || []));
      setTotalPages(Number(data?.meta?.pageMeta?.totalPages || 0));

      // 클라이언트 정렬로직 일단 만들어만 두고 사용은 상황 봐서
      // return sortMembers(list);

      return list;
    },
    [getMemberContractManageList, contract],
  );

  // 이용자 목록 검색
  const searchUserList = useCallback(
    async (searchValue?: string) => {
      const firstPage = 0;
      setPage(firstPage);
      const results = await loadUserList(firstPage, searchValue);
      setContractManages(results || []);

      if (!firstLoadUserListRef.current) {
        firstLoadUserListRef.current = true;
      }
    },
    [loadUserList],
  );

  // 이용자 목록 더 불러오기
  const loadMoreUserList = useCallback(async () => {
    if (contractManages.length > 0) {
      const nextPage = page + 1;
      setPage(nextPage);
      const results = await loadUserList(nextPage, searchText);
      setContractManages([...contractManages, ...(results || [])]);
    }
  }, [contractManages, page, loadUserList, searchText]);

  // 스크롤 감지
  const onIntersect: IntersectionObserverCallback = useCallback(
    ([entry]: IntersectionObserverEntry[]) => {
      // console.log("감지결과 :", entry.isIntersecting);
      if (entry.isIntersecting && !getMemberContractManageListLoading && page < totalPages - 1) {
        loadMoreUserList();
      }
    },
    [getMemberContractManageListLoading, loadMoreUserList, page, totalPages],
  );

  // 교차 감지 hook (scroll 감지)
  const { setTarget } = useIntersectionObserver({
    onIntersect,
    threshold: [0],
  });

  // debounce 최적화
  const debouncedSearch = useMemo(
    () =>
      debounce((_searchText?: string) => {
        if (contract?.contractManageId) {
          searchUserList(_searchText);
        }
      }, 200),
    [contract, searchUserList],
  );

  // 신청/계약 상세 조회
  const fetchContract = useCallback(
    async (contractId: string) => {
      const { data } = await getContract({ contractId });
      const contract = data?.data || null;

      setContract(contract);
    },
    [getContract],
  );

  // 이용자 초대 버튼 클릭
  const clickUserInvite = useCallback(() => {
    if (Number(contract?.memberMaxNums || 0) <= Number(contract?.memberTotCount || 0)) {
      // 최대 이용자수 초과시 alert
      setAlertModal({ isOpen: true });
      return;
    }
    // 이용자 초대 화면으로 이동
    navigate(`/court/mypage/contracts/${contractId}/users/invite`);
  }, [navigate, contract, contractId]);

  // 이용자 삭제 버튼 클릭
  const clickExcelDelete = useCallback(() => {
    // 이용자 삭제 화면으로 이동
    navigate(`/court/mypage/contracts/${contractId}/users/delete`);
  }, [navigate, contractId]);

  useEffect(() => {
    if (contractId) {
      // 신청/계약 상세 조회s
      fetchContract(contractId);
    }
  }, [contractId, fetchContract]);

  useEffect(() => {
    if (contract?.contractManageId) {
      // 계약의 이용자 목록 조회
      setSearchText("");
      searchUserList();
    }
  }, [contract, searchUserList]);

  // 이용자 재초대
  const reInvite = async (phoneNumber: string) => {
    try {
      await postMemberContractManageInvite({
        contractManageId: contract?.contractManageId!,
        contractMemberList: [
          {
            inviteMobileNumber: phoneNumber,
          },
        ],
      });

      setInviteSuccessModal({ isOpen: true });
      setInviteResults([
        {
          type: "SUCCESS",
          phoneNumber: phoneNumber,
        },
      ]);

      setSearchText("");
      searchUserList();
    } catch (error: any) {
      setInviteFailModal({ isOpen: true });

      console.log(error?.response?.data?.meta?.errorCode);
      setInviteResults([
        {
          type: "FAIL",
          phoneNumber: phoneNumber,
          failErrorCode: error?.response?.data?.meta?.errorCode,
        },
      ]);
    } finally {
      setInviteConfirmModal({ isOpen: false });
    }
  };

  // 리스트 클릭 - 이용자 재초대
  const clickUserItemList = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const target = e.target as HTMLElement;

    if (target.classList.contains("btn-reinvite")) {
      const phoneNumber = target.dataset.inviteMobileNumber;

      setClickedPhoneNumber(phoneNumber);
      setInviteConfirmModal({ isOpen: true });
    }
  };

  return (
    <>
      <MetaTag title={getMypageTitle()} />
      <Header
        headerType="BACK"
        title={t("userList.title")}
        onClickBackButton={() => {
          goBack();
        }}
      />

      <div className="user-invite-page">
        <div className="base-title-wrap">
          <h1 className="base-title">{t("userList.title")}</h1>
        </div>
        <div className="user-list-title-area">
          <div className="user-list-title-row">
            <h1 className="user-list-title">{t("userList.title")}</h1>
            <div className="user-list-title-buttons">
              <button type="button" className="base-btn size-small user-list-invite-button" onClick={() => clickUserInvite()}>
                <>
                  <p>{t("userList.inviteUser")}</p>
                  <div />
                </>
              </button>
              <button type="button" className="base-btn size-small user-list-invite-button" onClick={() => clickExcelDelete()}>
                <>
                  <p>{t("userList.excelDeleteUser")}</p>
                  <div />
                </>
              </button>
            </div>
          </div>
          <p ref={userListCountRef} className={`user-list-count ${isUserListCountCompact ? "user-list-count--compact" : ""}`.trim()}>
            {userListCountText}
          </p>
        </div>
        <section className="floating-input-area">
          <div className="base-input-wrap search-case">
            <input
              type="text"
              className="base-input"
              inputMode="email"
              placeholder={t("userList.enterMobileOrEmail")}
              value={searchText}
              onBlur={(e) => {
                const targetValue = e.target?.value.trim();
                const replacedValue = sanitizePhoneOrEmailSearch(targetValue);
                setSearchText(replacedValue);
                debouncedSearch(replacedValue);
              }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                e.preventDefault();
                const targetValue = e.target?.value;
                const replacedValue = sanitizePhoneOrEmailSearch(targetValue);
                setSearchText(replacedValue);
                debouncedSearch(replacedValue);
              }}
              onKeyUp={(e: React.KeyboardEvent<HTMLInputElement>) => {
                e.preventDefault();
                if (e.key === "Enter") {
                  if (contract?.contractManageId) {
                    searchUserList(searchText);
                  }
                }
              }}
            />

            {searchText.length > 0 ? (
              <button
                type="button"
                className="clear-btn"
                onClick={() => {
                  setSearchText("");
                  debouncedSearch();
                }}
              />
            ) : (
              <button
                type="button"
                className={`search-btn`}
                onClick={() => {
                  if (contract?.contractManageId) {
                    searchUserList();
                  }
                }}
              />
            )}
          </div>
        </section>
        <article className="user-invite-list">
          {firstLoadUserListRef.current && contractManages.length === 0 ? (
            <section className="contents-empty">
              <p>{t("userList.noUserFound")}</p>
            </section>
          ) : (
            <section className="contents-area" onClick={clickUserItemList}>
              {contractManages.map((contractManage: MemberContractManageModel, index: number) => (
                <UserItem
                  key={index}
                  contractManage={contractManage}
                  fetchContract={fetchContract}
                  setSearchText={setSearchText}
                  searchText={searchText}
                />
              ))}

              {/* 마지막까지 스크롤되었는지 확인하기 위한 엘리먼트 */}
              <div ref={setTarget}></div>
            </section>
          )}
          {getMemberContractManageListLoading && (
            <section className="contents-indicator">
              <Spinner size="page" />
            </section>
          )}
        </article>
        {/* {searchText.length === 0 && (
          <div className="base-floating-btn-wrap">
            <button type="button" className="base-btn" onClick={() => clickUserInvite()}>
              이용자 초대
            </button>
          </div>
        )} */}

        <ConfirmModal
          isOpen={alertModal.isOpen}
          btnRightTitle={t("common:confirm")}
          onClick={() => {
            setAlertModal({ isOpen: false });
          }}
        >
          <div>
            <p className="font18 font-weight-600">{t("inviteUser.maxUserExceeded")}</p>
            <p className="mt20 font15 font-weight-400">{t("inviteUser.maxUserExceededDesc")}</p>
          </div>
        </ConfirmModal>

        <ConfirmModal
          isOpen={inviteConfirmModal.isOpen}
          btnLeftTitle={t("common:cancel")}
          btnRightTitle={t("common:confirm")}
          onClose={() => setInviteConfirmModal({ isOpen: false })}
          onClick={() => reInvite(clickedPhoneNumber as string)}
          className="pa20 pt30"
        >
          <div>
            <p className="font18 font-weight-600">{t("inviteUser.inviteQuestion")}</p>
            <p className="font15 font-weight-400 mt20">{t("inviteUser.inviteAppInstallDesc")}</p>
            <p className="font15 font-weight-400 mt5">{t("inviteUser.inviteGrantDesc")}</p>
          </div>
        </ConfirmModal>

        <ConfirmModal
          isOpen={inviteSuccessModal.isOpen}
          btnRightTitle={t("common:confirm")}
          onClick={() =>
            setInviteSuccessModal({
              isOpen: false,
            })
          }
        >
          <div>
            <p className="font18 font-weight-600">{t("inviteUser.inviteCompleted")}</p>
          </div>
        </ConfirmModal>

        <ConfirmModal
          isOpen={inviteFailModal.isOpen}
          btnLeftTitle={t("common:close")}
          btnRightTitle={t("inviteUser.viewFailReason")}
          onClose={() => {
            setInviteFailModal({ isOpen: false });
          }}
          onClick={() => {
            navigate(`/court/mypage/contracts/${contractId}/users/invite/fail`, {
              state: {
                inviteResults,
              },
            });
          }}
        >
          <div>
            <p className="font18 font-weight-600">{t("inviteUser.inviteCompleted")}</p>
            <p className="mt20 font15 font-weight-400">
              {t("inviteUser.inviteSuccessFailCount", {
                success: inviteResults.filter((r) => r.type === "SUCCESS").length,
                fail: inviteResults.filter((r) => r.type === "FAIL").length,
              })}
            </p>
          </div>
        </ConfirmModal>
      </div>
    </>
  );
};
export default UserList;
